import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import RootLanding from '../../components/layouts/RootLanding';
import { getFetcherPublic } from '../../services/api';
import { BilahCari, LabelSeksi, Paginasi } from '../../components/fragments/landing/publik/Elemen';
import { JawabanFaq } from '../../components/fragments/landing/publik/Seksi';
import Ikon from '../../components/fragments/landing/publik/Ikon';
import { TOPIK } from '../../components/fragments/landing/publik/topikPublik';

/**
 * Halaman FAQ publik (frame Figma "FAQ").
 *
 * Isinya diambil dari CMS (menu CMS > FAQ) lewat rute yang sengaja tanpa
 * penjaga, sebab halaman ini dibuka pengunjung yang belum masuk. Hanya entri
 * bertanda aktif yang dikirim server, sudah terurut sesuai kolom "Urutan".
 *
 * Penyaringan dilakukan di browser: jumlah FAQ selalu kecil. FAQ tidak punya
 * kategori di basis data, jadi chip topik menyaring berdasarkan kata kunci topik
 * (`kataFaq`) pada pertanyaan dan jawaban.
 */
const PER_HALAMAN = 12;

export default function FaqPage() {
    const { data, isLoading } = useSWR('/api/faq/publik', getFetcherPublic);
    const [ketikan, setKetikan] = useState('');
    const [cari, setCari] = useState('');
    const [topik, setTopik] = useState(null);
    const [halaman, setHalaman] = useState(1);
    const [buka, setBuka] = useState(null);

    const daftar = useMemo(() => {
        const kataTopik = TOPIK.find((t) => t.kategori === topik)?.kataFaq;
        const kunci = cari.toLowerCase();
        return (data?.data ?? []).filter((f) => {
            const teks = `${f.pertanyaan} ${f.jawaban}`.toLowerCase();
            return (!kataTopik || teks.includes(kataTopik)) && (!kunci || teks.includes(kunci));
        });
    }, [data, cari, topik]);

    useEffect(() => setHalaman(1), [cari, topik]);

    const tampil = daftar.slice((halaman - 1) * PER_HALAMAN, halaman * PER_HALAMAN);
    const terbuka = buka ?? tampil[0]?.id;

    return (
        <RootLanding>
            <section className="bg-[#e9eee5]">
                <div className="mx-auto flex max-w-[1440px] flex-col gap-8 px-4 pb-16 pt-12 sm:px-8 lg:px-[72px]">
                    <div className="flex flex-col items-center gap-3 text-center">
                        <LabelSeksi>Bantuan</LabelSeksi>
                        <h1 className="text-4xl font-bold tracking-[-1px] text-pb-tombol md:text-5xl">FAQ</h1>
                    </div>

                    <div className="flex flex-col gap-5 rounded-2xl bg-white/70 p-5 md:p-6">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
                            <div className="lg:w-64">
                                <p className="text-lg font-semibold text-pb-tombol">Apa yang Anda cari?</p>
                                <p className="font-body text-[13px] text-pb-teks">Mulai dari kebutuhan Anda.</p>
                            </div>
                            <form
                                className="flex flex-1 gap-3"
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    setCari(ketikan.trim());
                                }}
                            >
                                <label className="flex h-12 flex-1 items-center gap-3 rounded-lg border border-pb-bingkai bg-white px-4 focus-within:border-pb-aksen">
                                    <Ikon nama="cari" className="size-4 shrink-0 text-ink-500" />
                                    <span className="sr-only">Cari pertanyaan</span>
                                    <input
                                        type="search"
                                        value={ketikan}
                                        onChange={(e) => {
                                            setKetikan(e.target.value);
                                            // Mengosongkan kotak langsung menampilkan semua lagi.
                                            if (!e.target.value) setCari('');
                                        }}
                                        placeholder="Cari layanan, informasi B3, atau dokumen..."
                                        className="w-full bg-transparent font-body text-sm text-pb-judul outline-none placeholder:text-ink-400"
                                    />
                                </label>
                                <button type="submit" className="h-12 rounded-md bg-pb-tombol px-[22px] font-body text-sm font-semibold text-white hover:bg-g-700">
                                    Cari
                                </button>
                            </form>
                        </div>
                        <BilahCari kategori={TOPIK.map((t) => t.kategori)} terpilih={topik} setTerpilih={setTopik} tanpaCari />
                    </div>

                    {isLoading ? (
                        <div className="flex flex-col gap-3">
                            {[0, 1, 2, 3].map((i) => (
                                <div key={i} className="h-[72px] animate-pulse rounded-xl bg-white/70" />
                            ))}
                        </div>
                    ) : tampil.length === 0 ? (
                        <p className="rounded-xl bg-white p-10 text-center font-body text-sm text-pb-teks">
                            {cari || topik ? 'Tidak ada pertanyaan yang cocok dengan pencarian Anda.' : 'Belum ada pertanyaan yang ditampilkan.'}
                        </p>
                    ) : (
                        <div className="flex flex-col gap-3">
                            {tampil.map((f) => {
                                const aktif = terbuka === f.id;
                                return (
                                    <div key={f.id} className={`rounded-xl border bg-white ${aktif ? 'border-pb-tombol' : 'border-transparent'}`}>
                                        <h2>
                                            <button
                                                type="button"
                                                aria-expanded={aktif}
                                                aria-controls={`faq-${f.id}`}
                                                onClick={() => setBuka(aktif ? '' : f.id)}
                                                className={`flex w-full items-center justify-between gap-6 px-5 py-6 text-left font-body md:px-7 ${
                                                    aktif ? 'text-lg font-semibold text-pb-tombol' : 'text-[17px] font-medium text-[#172e25]'
                                                }`}
                                            >
                                                {f.pertanyaan}
                                                <Ikon nama={aktif ? 'minus' : 'plus'} className="size-5 shrink-0 text-pb-tombol" tebal={1.6} />
                                            </button>
                                        </h2>
                                        {aktif && (
                                            <div id={`faq-${f.id}`} className="px-5 pb-7 md:px-7">
                                                <JawabanFaq teks={f.jawaban} className="text-[15px] text-[#59665e]" />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    <Paginasi halaman={halaman} jumlahHalaman={Math.ceil(daftar.length / PER_HALAMAN)} setHalaman={setHalaman} />
                </div>
            </section>
        </RootLanding>
    );
}
